import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AppBar, Box, Button, Toolbar, Typography, useTheme } from '@mui/material';
import storage from 'app/services/storage';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { DOCUMENT_FIELDS_BLOCK_WIDTH } from 'constants/documents';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getScrollStyles } from 'styles/scrollbar.styles';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  completeDocumentExtractionById,
  getDocumentExtractionFilledFieldsAction,
  getDocumentExtractionRequestAction,
  skipDocumentById,
} from 'store/documents/actions';
import { getDocumentExtractionSessionId } from 'store/documents/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import { getDocumentExtractionFieldsAction } from 'store/system/actions';
import { getDocumentExtractionFieldsWithNamedType } from 'store/system/selectors';

import { DocumentTestIds } from '../../document.testIds';

import {
  getExtractionFieldsToSend,
  getIsErrorStatusForbiddenOrNotFound,
} from './EditExtractionFields.utils';
import { LOCAL_STORAGE_FORM_KEY } from './EditExtractionFieldsForm.constants';
import EditExtractionFieldsFormFieldsPart from './EditExtractionFieldsFormFieldsPart';
import RejectDocumentForm from './RejectDocumentForm';

const editContainerStyles = {
  height: '100%',
  width: `${DOCUMENT_FIELDS_BLOCK_WIDTH}px`,
  minWidth: `${DOCUMENT_FIELDS_BLOCK_WIDTH}px`,
  borderLeft: ({ palette }) => `1px solid ${palette.divider}`,
  overflow: 'auto',
};

const editFormStyles = {
  height: '100%',
  width: '100%',
};

const appBarStyles = {
  height: '75px',
  zIndex: 100,
};

const EditExtractionFieldsForm = ({
  documentTypeId = '',
  isView = true,
  docId = '',
  setIsLoading,
  isLoading,
  aiModelFieldToFillInForm = null,
  setAiModelFieldToFillInForm,
  boundingBoxPopUpInfo = null,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = getScrollStyles(theme);
  const scrollClasses = getScrollStyles(theme);

  const documentExtractionFields = useSelector(getDocumentExtractionFieldsWithNamedType);

  const sessionId = useSelector(getDocumentExtractionSessionId);
  const [duplicatedFields, setDuplicatedFields] = useState([]);
  const [documentExtractionFieldsTree, setDocumentExtractionFieldsTree] = useState([]);

  const { isViewAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.DOCUMENT_EXTRACTION),
  );

  const { initialForm, ...formProps } = useForm({});
  const { form, validateForm, setForm, setValidationErrors } = formProps;

  const hasAvailableDocuments = !!sessionId;

  const isButtonDisabled = isLoading || !isEditAllowed || !documentExtractionFields.length;
  const isSkipButtonDisabled = isButtonDisabled || !hasAvailableDocuments;

  useEffectSafeStrictMode(() => {
    if (!documentTypeId || !isViewAllowed) {
      return;
    }

    dispatch(getDocumentExtractionFieldsAction({ documentTypeId })).catch(Error);
  }, [documentTypeId, dispatch, isViewAllowed]);

  useEffectSafeStrictMode(() => {
    if (!docId) {
      return;
    }

    if (isView || !isViewAllowed) {
      return;
    }

    setIsLoading(true);

    dispatch(getDocumentExtractionFilledFieldsAction({ documentId: docId }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [isView, docId, dispatch, setIsLoading, isViewAllowed]);

  const clearLocalStorage = useCallback(() => {
    storage.removeItem(LOCAL_STORAGE_FORM_KEY);
  }, []);

  const getAssignedDocumentWithReset = useCallback(async () => {
    clearLocalStorage();
    setForm({});

    await dispatch(getDocumentExtractionRequestAction());

    setDuplicatedFields([]);
  }, [clearLocalStorage, dispatch, setForm]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      const fields = getExtractionFieldsToSend({
        form,
        documentExtractionFieldsTree,
      });

      if (!fields.extractionFields?.length) {
        dispatch(
          showMessageAction({
            message: 'At least one field should be extracted',
            variant: 'error',
          }),
        );

        return;
      }

      const data = {
        results: fields.extractionFields,
        documentId: docId,
      };

      try {
        setIsLoading(true);

        await dispatch(completeDocumentExtractionById(data));
        await getAssignedDocumentWithReset();
      } catch (err) {
        logger.error(err);

        if (getIsErrorStatusForbiddenOrNotFound(err)) {
          await getAssignedDocumentWithReset();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      validateForm,
      form,
      documentExtractionFieldsTree,
      docId,
      dispatch,
      setIsLoading,
      getAssignedDocumentWithReset,
    ],
  );

  const onReject = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Add Reject Reason',
        body: dialogProps => (
          <RejectDocumentForm
            documentId={docId}
            handleReject={clearLocalStorage}
            {...dialogProps}
          />
        ),
      }),
    );
  }, [clearLocalStorage, dispatch, docId]);

  const onSkip = useCallback(() => {
    setIsLoading(true);

    dispatch(
      skipDocumentById({
        documentId: docId,
      }),
    )
      .then(() => {
        setValidationErrors({});

        return dispatch(getDocumentExtractionRequestAction());
      })
      .catch(async err => {
        logger.error(err);

        if (!getIsErrorStatusForbiddenOrNotFound(err)) {
          return;
        }

        dispatch(getDocumentExtractionRequestAction());
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, docId, setIsLoading, setValidationErrors]);

  if (!documentTypeId || !docId || isView || !isViewAllowed) {
    return null;
  }

  return (
    <Box sx={editContainerStyles}>
      <Box noValidate component="form" sx={editFormStyles} onSubmit={onSubmit}>
        <AppBar color="default" elevation={1} position="static" sx={appBarStyles}>
          <Typography className="px-10" color="inherit" variant="medium16">
            Fields
          </Typography>
          <Toolbar className="flex w-full justify-between min-h-1">
            <Button
              data-testid={DocumentTestIds.SKIP_DOCUMENT_BUTTON}
              disabled={isSkipButtonDisabled}
              type="button"
              variant="contained"
              onClick={onSkip}
            >
              Skip
            </Button>
            <div>
              <Button
                data-testid={DocumentTestIds.REJECT_DOCUMENT_BUTTON}
                disabled={isButtonDisabled}
                type="button"
                variant="contained"
                onClick={onReject}
              >
                Reject
              </Button>
              <Button
                className="whitespace-nowrap ml-5"
                data-testid={DocumentTestIds.EXTRACTION_FIELDS_SAVE_BUTTON}
                disabled={isButtonDisabled}
                type="submit"
                variant="contained"
              >
                Save
              </Button>
            </div>
          </Toolbar>
        </AppBar>

        <Box
          className={clsx('overflow-y-auto', scrollClasses.scroll)}
          sx={classes.editFieldsContainer}
        >
          <div
            className="flex flex-col justify-center pt-20 p-24"
            data-testid={DocumentTestIds.FIELDS}
          >
            <EditExtractionFieldsFormFieldsPart
              aiModelFieldToFillInForm={aiModelFieldToFillInForm}
              boundingBoxPopUpInfo={boundingBoxPopUpInfo}
              docId={docId}
              documentExtractionFieldsTree={documentExtractionFieldsTree}
              documentTypeId={documentTypeId}
              duplicatedFields={duplicatedFields}
              isLoading={isLoading}
              isView={isView}
              setAiModelFieldToFillInForm={setAiModelFieldToFillInForm}
              setDocumentExtractionFieldsTree={setDocumentExtractionFieldsTree}
              setDuplicatedFields={setDuplicatedFields}
              setIsLoading={setIsLoading}
              {...formProps}
            />
          </div>
        </Box>
      </Box>
    </Box>
  );
};

EditExtractionFieldsForm.propTypes = {
  setAiModelFieldToFillInForm: PropTypes.func.isRequired,
  boundingBoxPopUpInfo: PropTypes.object,
  aiModelFieldToFillInForm: PropTypes.object,
  documentTypeId: PropTypes.string,
  docId: PropTypes.string,
  isView: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default withLoading(memo(EditExtractionFieldsForm));
